import { MAPS, DIFFICULTIES, BIOMES, generateProceduralMap } from './maps.js';
import { looplay } from '../looplay/LooplayBridge.js';

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const lerp = (a, b, t) => a + (b - a) * t;
const rand = (min, max) => min + Math.random() * (max - min);
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const TERRAIN_SAMPLES = 600;

export class Game {
  constructor(canvas, ui) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d', { alpha: false });
    this.ui = ui;
    this.dpr = 1;
    this.running = false;
    this.last = performance.now();
    this.state = 'menu';
    this.save = this.loadSave();
    this.selectedMap = MAPS[Math.min(this.save.unlocked, MAPS.length - 1)];
    this.difficultyKey = 'normal';
    this.angle = 45;
    this.power = 65;
    this.wind = 0;
    this.turn = 'player';
    this.turnCount = 0;
    this.actingEnemy = null;
    this.activeMoveDir = 0;
    this.pendingExtraShots = 0;
    this.afterShotPending = false;

    // World & Camera
    this.worldW = 1200;
    this.worldH = 680;
    this.minSpeed = 330;
    this.maxSpeed = 830;
    this.baseGravity = 480;

    this.camera = {
      x: 350,
      y: 340,
      targetX: 350,
      targetY: 340,
      zoom: 0.8,
      targetZoom: 0.8,
      minZoom: 0.45,
      maxZoom: 1.55,
      manualTimer: 0
    };

    this.activePointers = new Map();
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.cameraStartX = 0;
    this.cameraStartY = 0;
    this.initialPinchDist = 0;
    this.initialPinchZoom = 1;
    this.camHintTimer = 4.5;

    this.projectiles = [];
    this.particles = [];
    this.effects = [];
    this.floatingTexts = [];
    this.terrainProfile = this.createTerrainProfile();
    this.shake = 0;
    this.message = '';
    this.messageTimer = 0;
    this.items = { heal: 2, double: 1, shield: 1 };
    this.rewardedItemRequestInFlight = false;
    this.roundGeneration = 0;
    this.doubleShot = false;
    this.playerShield = 0;

    this.bindUI();
    this.bindGestures();
    this.resize();

    this.resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => this.resize())
      : null;
    this.resizeObserver?.observe(this.canvas.parentElement);
    window.addEventListener('resize', () => this.resize(), { passive: true });

    this.running = true;
    requestAnimationFrame((t) => this.loop(t));
  }

  loadSave() {
    try {
      return { unlocked: 0, stars: {}, ...(JSON.parse(localStorage.getItem('windshot-save')) || {}) };
    } catch {
      return { unlocked: 0, stars: {} };
    }
  }

  persist() {
    localStorage.setItem('windshot-save', JSON.stringify(this.save));
  }

  bindUI() {
    this.ui.angle.addEventListener('input', () => {
      this.angle = Number(this.ui.angle.value);
      this.ui.angleValue.textContent = `${this.angle}°`;
    });
    this.ui.power.addEventListener('input', () => {
      this.power = Number(this.ui.power.value);
      this.ui.powerValue.textContent = `${this.power}%`;
    });
    this.ui.fire.addEventListener('click', () => this.playerFire());
    this.ui.heal.addEventListener('click', () => this.useItem('heal'));
    this.ui.double.addEventListener('click', () => this.useItem('double'));
    this.ui.shield.addEventListener('click', () => this.useItem('shield'));
    this.ui.menuBtn.addEventListener('click', () => this.showMenu());

    this.ui.zoomInBtn?.addEventListener('click', () => this.zoomIn());
    this.ui.zoomOutBtn?.addEventListener('click', () => this.zoomOut());
    this.ui.camFocusBtn?.addEventListener('click', () => this.focusPlayer());
    this.bindMoveControls();
  }

  bindMoveControls() {
    const bindBtn = (btn, dir) => {
      if (!btn) return;
      const start = (e) => {
        e.preventDefault();
        if (this.state !== 'playing' || this.turn !== 'player' || this.projectiles.length > 0) return;
        this.activeMoveDir = dir;
        if (this.player) this.player.facing = dir;
      };
      const stop = (e) => {
        if (this.activeMoveDir === dir) {
          this.activeMoveDir = 0;
          if (this.player) this.player.isMoving = false;
        }
      };

      btn.addEventListener('pointerdown', start);
      btn.addEventListener('pointerup', stop);
      btn.addEventListener('pointercancel', stop);
      btn.addEventListener('pointerleave', stop);
      btn.addEventListener('contextmenu', (e) => e.preventDefault());
    };

    bindBtn(this.ui.moveLeft, -1);
    bindBtn(this.ui.moveRight, 1);

    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', (e) => {
        if (this.state !== 'playing' || this.turn !== 'player' || this.projectiles.length > 0) return;
        if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT')) return;

        if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
          this.activeMoveDir = -1;
          if (this.player) this.player.facing = -1;
        } else if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
          this.activeMoveDir = 1;
          if (this.player) this.player.facing = 1;
        }
      });

      window.addEventListener('keyup', (e) => {
        if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
          if (this.activeMoveDir === -1) {
            this.activeMoveDir = 0;
            if (this.player) this.player.isMoving = false;
          }
        } else if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
          if (this.activeMoveDir === 1) {
            this.activeMoveDir = 0;
            if (this.player) this.player.isMoving = false;
          }
        }
      });

      window.addEventListener('blur', () => {
        this.activeMoveDir = 0;
        if (this.player) this.player.isMoving = false;
      });
    }
  }

  bindGestures() {
    const canvas = this.canvas;

    canvas.addEventListener('pointerdown', (e) => {
      canvas.setPointerCapture(e.pointerId);
      this.activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.ui.camHint && !this.ui.camHint.classList.contains('fade-out')) {
        this.ui.camHint.classList.add('fade-out');
      }

      if (this.activePointers.size === 1) {
        this.isDragging = true;
        this.dragStartX = e.clientX;
        this.dragStartY = e.clientY;
        this.cameraStartX = this.camera.targetX;
        this.cameraStartY = this.camera.targetY;
      } else if (this.activePointers.size === 2) {
        this.isDragging = false;
        const [p1, p2] = Array.from(this.activePointers.values());
        this.initialPinchDist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
        this.initialPinchZoom = this.camera.targetZoom;
      }
    });

    canvas.addEventListener('pointermove', (e) => {
      if (!this.activePointers.has(e.pointerId)) return;
      this.activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.activePointers.size === 2 && this.initialPinchDist > 0) {
        const [p1, p2] = Array.from(this.activePointers.values());
        const d = Math.hypot(p1.x - p2.x, p1.y - p2.y);
        const ratio = d / this.initialPinchDist;
        this.camera.targetZoom = clamp(this.initialPinchZoom * ratio, this.camera.minZoom, this.camera.maxZoom);
        this.camera.manualTimer = 4.0;
      } else if (this.isDragging && this.activePointers.size === 1) {
        const dx = (e.clientX - this.dragStartX) / this.camera.zoom;
        const dy = (e.clientY - this.dragStartY) / this.camera.zoom;
        this.camera.targetX = this.cameraStartX - dx;
        this.camera.targetY = this.cameraStartY - dy;
        this.clampCameraTarget();
        this.camera.manualTimer = 4.0;
      }
    });

    const onPointerEnd = (e) => {
      this.activePointers.delete(e.pointerId);
      if (this.activePointers.size === 0) {
        this.isDragging = false;
        this.initialPinchDist = 0;
      } else if (this.activePointers.size === 1) {
        const [p] = Array.from(this.activePointers.values());
        this.dragStartX = p.x;
        this.dragStartY = p.y;
        this.cameraStartX = this.camera.targetX;
        this.cameraStartY = this.camera.targetY;
        this.isDragging = true;
      }
    };

    canvas.addEventListener('pointerup', onPointerEnd);
    canvas.addEventListener('pointercancel', onPointerEnd);

    canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.15 : 0.85;
      this.camera.targetZoom = clamp(this.camera.targetZoom * factor, this.camera.minZoom, this.camera.maxZoom);
      this.camera.manualTimer = 4.0;
    }, { passive: false });
  }

  zoomIn() {
    this.camera.targetZoom = clamp(this.camera.targetZoom * 1.25, this.camera.minZoom, this.camera.maxZoom);
    this.camera.manualTimer = 4.0;
    this.ui.camHint?.classList.add('fade-out');
  }

  zoomOut() {
    this.camera.targetZoom = clamp(this.camera.targetZoom / 1.25, this.camera.minZoom, this.camera.maxZoom);
    this.camera.manualTimer = 4.0;
    this.ui.camHint?.classList.add('fade-out');
  }

  focusPlayer() {
    if (this.player) {
      this.camera.targetX = this.player.x + 100 * (this.player.facing || 1);
      this.camera.targetY = this.player.y - 30;
      this.camera.manualTimer = 0;
      this.ui.camHint?.classList.add('fade-out');
    }
  }

  clampCameraTarget() {
    const halfVisibleW = (this.logicalW / 2) / this.camera.zoom;
    const halfVisibleH = (this.logicalH / 2) / this.camera.zoom;

    if (halfVisibleW * 2 >= this.worldW) {
      this.camera.targetX = this.worldW / 2;
    } else {
      this.camera.targetX = clamp(this.camera.targetX, halfVisibleW - 60, this.worldW - halfVisibleW + 60);
    }

    if (halfVisibleH * 2 >= this.worldH) {
      this.camera.targetY = this.worldH / 2;
    } else {
      this.camera.targetY = clamp(this.camera.targetY, halfVisibleH - 80, this.worldH - halfVisibleH + 80);
    }
  }

  setMap(map) {
    this.selectedMap = map;
    this.terrainProfile = this.createTerrainProfile();
  }

  setRandomMap(biomeKey = 'random') {
    const map = generateProceduralMap({
      biomeKey,
      difficultyKey: this.difficultyKey,
      seed: Math.random()
    });
    this.setMap(map);
    return map;
  }

  rerollCurrentMap() {
    if (this.selectedMap?.isProcedural) {
      return this.setRandomMap(this.selectedMap.biomeKey || 'random');
    }
    const biomeKey = this.selectedMap?.id || 'meadow';
    const map = generateProceduralMap({
      biomeKey,
      difficultyKey: this.difficultyKey,
      seed: Math.random(),
      hasBoss: this.selectedMap?.boss
    });
    map.name = `${this.selectedMap?.name || 'Battlefield'} (Reroll)`;
    this.setMap(map);
    return map;
  }

  setDifficulty(key) {
    if (DIFFICULTIES[key]) this.difficultyKey = key;
  }

  resize() {
    const wrap = this.canvas.parentElement;
    if (!wrap) return;
    const box = wrap.getBoundingClientRect();
    if (box.width < 1 || box.height < 1) return;

    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.logicalW = Math.max(280, box.width);
    this.logicalH = Math.max(260, box.height);
    const pixelW = Math.round(this.logicalW * this.dpr);
    const pixelH = Math.round(this.logicalH * this.dpr);

    if (this.canvas.width !== pixelW || this.canvas.height !== pixelH) {
      this.canvas.width = pixelW;
      this.canvas.height = pixelH;
    }
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    // Compute comfortable zoom for current viewport
    this.camera.minZoom = clamp(Math.min(this.logicalW / (this.worldW * 1.05), this.logicalH / this.worldH), 0.35, 0.75);
    if (this.state !== 'playing') {
      const defaultFitZoom = clamp(this.logicalW / 620, 0.62, 1.05);
      this.camera.zoom = defaultFitZoom;
      this.camera.targetZoom = defaultFitZoom;
    }
    if (this.player) this.snapActorsToTerrain();
  }

  createTerrainProfile() {
    const t = this.selectedMap?.terrain ?? MAPS[0].terrain;
    const topo = t.topography || 'rolling';
    const profile = new Float32Array(TERRAIN_SAMPLES);
    const seedShift = t.seed ? (t.seed * 100) % (Math.PI * 2) : 0;
    const phase = (t.phase || 0) + seedShift;

    for (let i = 0; i < profile.length; i++) {
      const x01 = i / (profile.length - 1);

      // Base multi-harmonic natural waves
      let y = t.base
        + Math.sin((x01 * t.freq1 + phase) * Math.PI * 2) * t.amp1
        + Math.sin((x01 * t.freq2 + phase * 0.7) * Math.PI * 2) * (t.amp2 || 0.03)
        + Math.sin((x01 * 12.0 + phase * 1.4) * Math.PI * 2) * 0.012; // fine crag ridges

      // Topography shaping
      if (topo === 'canyon') {
        // Deep ravine in the middle with cliffs on sides
        const canyonDip = Math.sin(x01 * Math.PI) * 0.12;
        y += canyonDip;
      } else if (topo === 'twin_peaks') {
        // Ridge/mountain peak in the center separating players
        const centerPeak = Math.sin(x01 * Math.PI) * 0.10;
        y -= centerPeak;
      } else if (topo === 'dunes') {
        // Windward dunes with asymmetric slopes
        const duneSaw = (x01 * 3.5 + phase * 0.3) % 1;
        y += (Math.pow(duneSaw, 1.8) - 0.5) * 0.055;
      } else if (topo === 'crater') {
        // Concave impact basin
        const craterDist = Math.abs(x01 - 0.55);
        if (craterDist < 0.28) {
          const rim = Math.cos((craterDist / 0.28) * Math.PI * 0.5);
          y += rim * 0.09;
        }
      } else if (topo === 'plateau') {
        // Stepped tableland
        const step = Math.sin(x01 * Math.PI * 4 + phase);
        y += Math.sign(step) * Math.pow(Math.abs(step), 0.4) * 0.04;
      }

      profile[i] = clamp(y, 0.44, 0.88);
    }
    return profile;
  }

  terrainY01(x01) {
    const x = clamp(x01, 0, 1) * (this.terrainProfile.length - 1);
    const i = Math.floor(x);
    const j = Math.min(this.terrainProfile.length - 1, i + 1);
    return lerp(this.terrainProfile[i], this.terrainProfile[j], x - i);
  }

  terrainYPx(worldX) {
    return this.terrainY01(clamp(worldX / this.worldW, 0, 1)) * this.worldH;
  }

  deformTerrain(worldX, radiusPx, depthPx) {
    const center01 = clamp(worldX / this.worldW, 0, 1);
    const radius01 = radiusPx / this.worldW;
    const depth01 = depthPx / this.worldH;

    for (let i = 0; i < this.terrainProfile.length; i++) {
      const x01 = i / (this.terrainProfile.length - 1);
      const dx = Math.abs(x01 - center01);
      if (dx >= radius01) continue;
      const n = dx / radius01;
      const bowl = Math.pow(1 - n * n, 1.55);
      this.terrainProfile[i] = clamp(this.terrainProfile[i] + depth01 * bowl, 0.45, 0.95);
    }
  }

  start() {
    const map = this.selectedMap;
    const diff = DIFFICULTIES[this.difficultyKey] || DIFFICULTIES.normal;
    this.roundGeneration++;
    this.state = 'playing';
    this.turn = 'player';
    this.turnCount = 0;
    this.actingEnemy = null;
    this.projectiles.length = 0;
    this.particles.length = 0;
    this.effects.length = 0;
    this.floatingTexts.length = 0;
    this.terrainProfile = this.createTerrainProfile();
    this.items = { heal: 2, double: 1, shield: 1 };
    this.doubleShot = false;
    this.playerShield = 0;
    this.pendingExtraShots = 0;
    this.afterShotPending = false;
    this.wind = this.randomWind();
    this.activeMoveDir = 0;

    this.player = {
      id: 'player',
      x: 0.12 * this.worldW,
      y: 0,
      hp: 100,
      maxHp: 100,
      radius: 17,
      alive: true,
      recoil: 0,
      flash: 0,
      facing: 1,
      stamina: 100,
      maxStamina: 100,
      isMoving: false,
      walkTime: 0,
      dustTimer: 0
    };
    this.enemies = [];

    const mapIndex = MAPS.findIndex((m) => m.id === map.id);
    const mapTier = map.isProcedural ? Math.max(1, (this.save.unlocked || 0) + 1) : Math.max(1, mapIndex + 1);
    const count = map.enemies || 3;
    for (let i = 0; i < count; i++) {
      const boss = map.boss && i === count - 1;
      const x01 = count === 1 ? 0.78 : lerp(0.62, 0.88, i / Math.max(1, count - 1));
      const maxHp = Math.round((boss ? 200 : 80) * diff.hp * (1 + (mapTier - 1) * 0.08));
      this.enemies.push({
        id: `${map.id}-${i}`,
        x: x01 * this.worldW,
        y: 0,
        hp: maxHp,
        maxHp,
        radius: boss ? 28 : 16,
        alive: true,
        boss,
        name: boss ? 'KING TANK' : `Enemy ${i + 1}`,
        recoil: 0,
        flash: 0
      });
    }

    this.snapActorsToTerrain();
    this.ui.overlay.classList.add('hidden');
    this.ui.controls.classList.remove('hidden');
    this.ui.mapName.textContent = `${map.icon} ${map.name} · ${diff.name}`;
    this.ui.stageChip.textContent = `${map.icon} ${map.name}`;
    this.updateHUD();
    this.setMessage(`${map.icon} ${map.name} · ${diff.name}`);

    // Set camera initially on player with comfortable zoom
    const fitZoom = clamp(this.logicalW / 640, 0.65, 1.05);
    this.camera.zoom = fitZoom;
    this.camera.targetZoom = fitZoom;
    this.camera.targetX = this.player.x + 110;
    this.camera.targetY = this.player.y - 30;
    this.camera.x = this.camera.targetX;
    this.camera.y = this.camera.targetY;
    this.camera.manualTimer = 0;
    this.camHintTimer = 4.5;
    this.ui.camHint?.classList.remove('fade-out');

    looplay.startSession({
      matchId: crypto.randomUUID(),
      mapId: map.id,
      level: mapTier,
      difficulty: this.difficultyKey
    });

    requestAnimationFrame(() => this.resize());
  }

  showMenu() {
    if (this.state === 'playing') {
      looplay.stop({ reason: 'menu', mapId: this.selectedMap?.id });
    }
    this.state = 'menu';
    this.projectiles.length = 0;
    this.effects.length = 0;
    this.floatingTexts.length = 0;
    this.ui.controls.classList.add('hidden');
    this.ui.overlay.classList.remove('hidden');
    this.ui.showMenu();
  }

  randomWind() {
    const strength = this.selectedMap.wind * DIFFICULTIES[this.difficultyKey].wind;
    return rand(-1, 1) * strength;
  }

  actorPx(actor) {
    return { x: actor.x, y: actor.y };
  }

  snapActorsToTerrain() {
    const all = [this.player, ...(this.enemies || [])].filter(Boolean);
    for (const actor of all) {
      actor.y = this.terrainYPx(actor.x) - actor.radius;
    }
  }

  playerFire() {
    if (this.state !== 'playing' || this.turn !== 'player' || this.projectiles.length) return;
    this.turnCount++;
    this.camera.manualTimer = 0;
    this.activeMoveDir = 0;
    this.player.isMoving = false;
    this.player.recoil = 0.35;

    const facing = this.player.facing || 1;
    const p = this.actorPx(this.player);
    this.spawnProjectile({
      owner: 'player',
      x: p.x + 22 * facing,
      y: p.y - 12,
      angle: this.angle,
      power: this.power,
      dir: facing,
      damage: 46,
      color: '#f8fafc'
    });

    if (this.doubleShot) {
      this.doubleShot = false;
      this.pendingExtraShots++;
      setTimeout(() => {
        this.pendingExtraShots = Math.max(0, this.pendingExtraShots - 1);
        if (this.state !== 'playing') return;
        this.player.recoil = 0.35;
        this.spawnProjectile({
          owner: 'player',
          x: p.x + 22 * facing,
          y: p.y - 12,
          angle: this.angle + 2,
          power: this.power * 0.96,
          dir: facing,
          damage: 36,
          color: '#fde68a'
        });
      }, 140);
    }

    this.turn = 'animating';
    this.updateHUD();
  }

  spawnProjectile({ owner, x, y, angle, power, dir, damage, color, isBoss = false, blastRadius, craterRadius, craterDepth }) {
    const speed = lerp(this.minSpeed, this.maxSpeed, clamp(power, 1, 100) / 100);
    const rad = (angle * Math.PI) / 180;
    this.projectiles.push({
      owner,
      x,
      y,
      prevX: x,
      prevY: y,
      vx: Math.cos(rad) * speed * dir,
      vy: -Math.sin(rad) * speed,
      radius: isBoss ? 8 : 6,
      damage,
      color,
      isBoss,
      blastRadius,
      craterRadius,
      craterDepth,
      life: 0,
      trail: []
    });
  }

  useItem(type) {
    if (this.state !== 'playing' || this.turn !== 'player') return;
    if (this.items[type] <= 0) {
      void this.requestItemWithRewardedAd(type);
      return;
    }

    if (type === 'heal') {
      if (this.player.hp >= this.player.maxHp) return;
      this.items.heal--;
      this.player.hp = Math.min(this.player.maxHp, this.player.hp + 32);
      this.player.flash = 0.2;
      this.addFloatingText(this.player.x, this.player.y - 28, '+32 HP', '#34d399');
      this.setMessage('❤️ Restored 32 HP');
    } else if (type === 'double') {
      if (this.doubleShot) return;
      this.items.double--;
      this.doubleShot = true;
      this.setMessage('✨ Next shot fires 2 projectiles');
    } else if (type === 'shield') {
      if (this.playerShield > 0) return;
      this.items.shield--;
      this.playerShield = 1;
      this.setMessage('🛡️ Block 55% incoming damage next turn');
    }
    this.updateHUD();
  }

  async requestItemWithRewardedAd(type) {
    if (!looplay.canRequestRewardedAd || this.rewardedItemRequestInFlight) return;

    const roundGeneration = this.roundGeneration;
    this.rewardedItemRequestInFlight = true;
    this.turn = 'ad';
    this.updateHUD();
    const watched = await looplay.requestRewardedAd();
    this.rewardedItemRequestInFlight = false;

    if (this.state !== 'playing' || roundGeneration !== this.roundGeneration) return;
    this.turn = 'player';
    if (watched) {
      this.items[type]++;
      this.setMessage(`✨ +1 ${type} item`);
    } else {
      this.setMessage('Ad unavailable · Try again');
    }
    this.updateHUD();
  }

  chooseEnemy() {
    const living = this.enemies.filter((e) => e.alive);
    return living[Math.floor(Math.random() * living.length)];
  }

  calculateAiShot(shooter, target) {
    const s = this.actorPx(shooter);
    const t = this.actorPx(target);
    const diff = DIFFICULTIES[this.difficultyKey] || DIFFICULTIES.normal;
    const gravity = this.baseGravity * (this.selectedMap.gravity ?? 1);
    const windAccel = this.wind * 50 * (diff.windComp ?? 0.82);
    const facing = shooter.x < target.x ? 1 : -1;

    let bestAngle = 45;
    let bestPower = 60;
    let minError = Infinity;

    // Evaluate multiple ballistic arc angles
    const testAngles = [34, 42, 50, 58, 65, 72];
    for (const testAngle of testAngles) {
      const rad = (testAngle * Math.PI) / 180;

      // Binary search for optimal power to reach target X
      let lowPower = 25;
      let highPower = 98;
      for (let step = 0; step < 7; step++) {
        const midPower = (lowPower + highPower) / 2;
        const speed = lerp(this.minSpeed, this.maxSpeed, midPower / 100);

        let simX = s.x + 20 * facing;
        let simY = s.y - 10;
        let vx = Math.cos(rad) * speed * facing;
        let vy = -Math.sin(rad) * speed;
        let closestDist = Infinity;

        for (let i = 0; i < 48; i++) {
          const dt = 0.045;
          vx += windAccel * dt;
          vy += gravity * dt;
          simX += vx * dt;
          simY += vy * dt;

          const d = dist({ x: simX, y: simY }, t);
          if (d < closestDist) closestDist = d;
          if (simY > this.terrainYPx(simX) || simX < 0 || simX > this.worldW) break;
        }

        if (closestDist < minError) {
          minError = closestDist;
          bestAngle = testAngle;
          bestPower = midPower;
        }

        const reachedTargetX = facing > 0 ? simX >= t.x : simX <= t.x;
        if (reachedTargetX) {
          highPower = midPower;
        } else {
          lowPower = midPower;
        }
      }
    }

    // Apply difficulty-tailored variance
    const errorFactor = diff.aiError ?? 0.12;
    const angleNoise = rand(-7, 7) * errorFactor * 3.5;
    const powerNoise = rand(-10, 10) * errorFactor * 3.2;

    return {
      angle: clamp(bestAngle + angleNoise, 25, 78),
      power: clamp(bestPower + powerNoise, 20, 100)
    };
  }

  enemyTurn() {
    if (this.state !== 'playing') return;
    const shooter = this.chooseEnemy();
    if (!shooter) return this.finish(true);

    this.turn = 'enemy';
    this.actingEnemy = shooter;
    this.camera.manualTimer = 0;
    this.camera.targetX = shooter.x - 80;
    this.camera.targetY = shooter.y - 20;
    this.updateHUD();

    const diff = DIFFICULTIES[this.difficultyKey] || DIFFICULTIES.normal;
    const { angle, power } = this.calculateAiShot(shooter, this.player);

    const isBoss = !!shooter.boss;
    const isEnraged = isBoss && shooter.hp < shooter.maxHp * 0.45;
    const isBossSpecial = isBoss && diff.allowBossSpecial && (isEnraged || Math.random() < 0.38);
    const facing = shooter.x < this.player.x ? 1 : -1;

    setTimeout(() => {
      if (this.state !== 'playing' || !shooter.alive) return;
      shooter.recoil = 0.4;
      const s = this.actorPx(shooter);

      if (isBossSpecial) {
        this.addFloatingText(shooter.x, shooter.y - shooter.radius - 16, isEnraged ? '🔥 ENRAGED VOLLEY!' : '💥 TWIN BARRAGE!', '#ef4444');
      }

      this.spawnProjectile({
        owner: 'enemy',
        x: s.x + (shooter.radius + 8) * facing,
        y: s.y - 6,
        angle,
        power,
        dir: facing,
        damage: (isBoss ? 42 : 28) * diff.enemyDamage,
        color: isBoss ? '#fb7185' : '#fecaca',
        isBoss,
        blastRadius: isBoss ? 52 : 36,
        craterRadius: isBoss ? 78 : 52,
        craterDepth: isBoss ? 44 : 26
      });

      // Boss twin barrage secondary shell
      if (isBossSpecial) {
        this.pendingExtraShots++;
        setTimeout(() => {
          this.pendingExtraShots = Math.max(0, this.pendingExtraShots - 1);
          if (this.state !== 'playing' || !shooter.alive) return;
          shooter.recoil = 0.45;
          const s2 = this.actorPx(shooter);
          this.spawnProjectile({
            owner: 'enemy',
            x: s2.x + (shooter.radius + 8) * facing,
            y: s2.y - 6,
            angle: clamp(angle + rand(-4, 4), 25, 75),
            power: clamp(power + rand(-6, 6), 25, 98),
            dir: facing,
            damage: 26 * diff.enemyDamage,
            color: '#fbbf24',
            isBoss,
            blastRadius: 42,
            craterRadius: 60,
            craterDepth: 34
          });
        }, 340);
      }

      this.turn = 'animating';
      this.updateHUD();
    }, 600);
  }

  afterShot(owner) {
    if (this.state !== 'playing') return;
    this.snapActorsToTerrain();
    if (!this.player.alive || this.player.hp <= 0) return this.finish(false);
    if (!this.enemies.some((e) => e.alive)) return this.finish(true);

    this.wind = this.randomWind();
    if (owner === 'player') {
      this.turn = 'enemy';
      this.updateHUD();
      setTimeout(() => this.enemyTurn(), 560);
    } else {
      this.turn = 'player';
      this.actingEnemy = null;
      if (this.player) {
        this.player.stamina = this.player.maxStamina;
        this.player.isMoving = false;
      }
      this.activeMoveDir = 0;
      this.camera.manualTimer = 0;
      this.camera.targetX = this.player.x + 100 * (this.player.facing || 1);
      this.camera.targetY = this.player.y - 30;
      this.updateHUD();
      this.setMessage('Your Turn');
    }
  }

  explode(p) {
    const isPlayer = p.owner === 'player';
    const isBoss = !!p.isBoss;
    const blast = p.blastRadius || (isPlayer ? 68 : isBoss ? 52 : 38);
    this.shake = isPlayer ? 12 : isBoss ? 16 : 9;

    const surfaceY = this.terrainYPx(p.x);
    const closeToGround = p.x >= 0 && p.x <= this.worldW && p.y >= surfaceY - blast * 0.95;
    if (closeToGround) {
      const craterRadius = p.craterRadius || (isPlayer ? 65 : isBoss ? 78 : 50);
      const craterDepth = p.craterDepth || (isPlayer ? 36 : isBoss ? 44 : 26);
      this.deformTerrain(p.x, craterRadius, craterDepth);
    }

    this.effects.push({
      x: p.x,
      y: p.y,
      maxRadius: blast,
      duration: 0.32,
      age: 0
    });

    for (let i = 0; i < 28; i++) {
      const a = Math.random() * Math.PI * 2;
      const spd = rand(60, 240);
      this.particles.push({
        x: p.x,
        y: p.y,
        vx: Math.cos(a) * spd,
        vy: Math.sin(a) * spd,
        color: ['#fb923c', '#fde047', '#ef4444', '#f8fafc'][Math.floor(Math.random() * 4)],
        size: rand(3, 6),
        life: 0.35 + Math.random() * 0.25,
        max: 0.6
      });
    }

    const hitCandidates = p.owner === 'player' ? this.enemies : [this.player];
    let bestHit = 0;

    for (const actor of hitCandidates) {
      if (!actor.alive) continue;
      const actorCenter = this.actorPx(actor);
      const d = dist(actorCenter, p);
      if (d > blast + actor.radius) continue;

      const factor = clamp(1 - d / (blast + actor.radius), 0.2, 1);
      let damage = Math.round(p.damage * factor);
      if (actor === this.player && this.playerShield > 0) {
        damage = Math.round(damage * 0.45);
        this.playerShield = 0;
      }
      actor.hp = Math.max(0, actor.hp - damage);
      actor.flash = 0.2;
      if (actor.hp <= 0) actor.alive = false;
      bestHit = Math.max(bestHit, damage);

      this.addFloatingText(actor.x, actor.y - actor.radius - 12, `-${damage}`, damage >= 40 ? '#f43f5e' : '#fb923c');
    }

    this.snapActorsToTerrain();
    this.setMessage(bestHit > 0 ? `💥 -${bestHit} HP` : closeToGround ? '💣 Terrain destroyed!' : '💨 Missed target');
    this.updateHUD();
  }

  addFloatingText(x, y, text, color = '#fb923c') {
    this.floatingTexts.push({
      x,
      y,
      text,
      color,
      vy: -42,
      alpha: 1,
      life: 0,
      duration: 1.1
    });
  }

  projectileCollision(p) {
    if (p.x < -120 || p.x > this.worldW + 120 || p.y > this.worldH + 120 || p.life > 10) {
      return { out: true, actor: null };
    }

    const candidates = p.owner === 'player' ? this.enemies : [this.player];
    for (const actor of candidates) {
      if (!actor.alive) continue;
      if (dist(this.actorPx(actor), p) < actor.radius + p.radius + 4) return { out: false, actor };
    }

    if (p.x >= 0 && p.x <= this.worldW && p.y + p.radius >= this.terrainYPx(p.x)) {
      return { out: false, actor: null, terrain: true };
    }

    return null;
  }

  update(dt) {
    if (this.messageTimer > 0) this.messageTimer -= dt;
    this.shake = Math.max(0, this.shake - dt * 32);

    if (this.camHintTimer > 0) {
      this.camHintTimer -= dt;
      if (this.camHintTimer <= 0) this.ui.camHint?.classList.add('fade-out');
    }

    // Update player movement & stamina
    this.updatePlayerMovement(dt);

    // Camera auto-tracking
    if (this.camera.manualTimer > 0) {
      this.camera.manualTimer -= dt;
    } else {
      if (this.projectiles.length > 0) {
        const lead = this.projectiles[0];
        this.camera.targetX = lead.x;
        this.camera.targetY = lead.y;
      } else if (this.turn === 'enemy' && this.actingEnemy) {
        this.camera.targetX = this.actingEnemy.x - 80;
        this.camera.targetY = this.actingEnemy.y - 20;
      } else if (this.turn === 'player' && this.player) {
        this.camera.targetX = this.player.x + 100 * (this.player.facing || 1);
        this.camera.targetY = this.player.y - 30;
      }
    }
    this.clampCameraTarget();

    // Smooth camera lerp
    const camLerp = 1 - Math.exp(-8 * dt);
    this.camera.x += (this.camera.targetX - this.camera.x) * camLerp;
    this.camera.y += (this.camera.targetY - this.camera.y) * camLerp;
    this.camera.zoom += (this.camera.targetZoom - this.camera.zoom) * camLerp;

    // Actor cooldowns
    if (this.player) {
      if (this.player.recoil > 0) this.player.recoil = Math.max(0, this.player.recoil - dt * 2.8);
      if (this.player.flash > 0) this.player.flash = Math.max(0, this.player.flash - dt);
    }
    for (const e of this.enemies) {
      if (e.recoil > 0) e.recoil = Math.max(0, e.recoil - dt * 2.8);
      if (e.flash > 0) e.flash = Math.max(0, e.flash - dt);
    }

    // Floating combat texts
    for (let i = this.floatingTexts.length - 1; i >= 0; i--) {
      const ft = this.floatingTexts[i];
      ft.life += dt;
      ft.y += ft.vy * dt;
      ft.vy += 22 * dt;
      ft.alpha = Math.max(0, 1 - (ft.life / ft.duration));
      if (ft.life >= ft.duration) this.floatingTexts.splice(i, 1);
    }

    const gravity = this.baseGravity * (this.selectedMap.gravity ?? 1);
    const windAccel = this.wind * 50;
    let resolvedOwner = null;

    for (const p of this.projectiles) {
      p.life += dt;
      p.prevX = p.x;
      p.prevY = p.y;
      p.vx += windAccel * dt;
      p.vy += gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;

      p.trail.push({ x: p.x, y: p.y });
      if (p.trail.length > 18) p.trail.shift();

      const col = this.projectileCollision(p);
      if (col) {
        p.dead = true;
        resolvedOwner = p.owner;
        if (!col.out) this.explode(p);
      }
    }

    this.projectiles = this.projectiles.filter((p) => !p.dead);

    for (const fx of this.effects) fx.age += dt;
    this.effects = this.effects.filter((fx) => fx.age < fx.duration);

    for (const q of this.particles) {
      q.x += q.vx * dt;
      q.y += q.vy * dt;
      q.vy += 320 * dt;
      q.life -= dt;
    }
    this.particles = this.particles.filter((q) => q.life > 0);

    if (resolvedOwner && !this.projectiles.length && !this.pendingExtraShots && !this.afterShotPending) {
      this.afterShotPending = true;
      setTimeout(() => {
        this.afterShotPending = false;
        this.afterShot(resolvedOwner);
      }, 260);
    }
  }

  finish(won) {
    this.state = 'over';
    const livingHp = Math.max(0, this.player.hp);
    const score = Math.round(
      (won ? 2600 : 700)
      + livingHp * 24
      - this.turnCount * 45
      + Object.values(this.items).reduce((a, b) => a + b, 0) * 120
    );

    let stars = 0;
    if (won) stars = livingHp >= 70 && this.turnCount <= 9 ? 3 : livingHp >= 35 ? 2 : 1;
    const mapIndex = MAPS.findIndex((m) => m.id === this.selectedMap.id);
    const levelNumber = mapIndex >= 0 ? mapIndex + 1 : Math.max(1, (this.save.unlocked || 0) + 1);

    if (won) {
      this.save.stars[this.selectedMap.id] = Math.max(stars, this.save.stars[this.selectedMap.id] || 0);
      if (mapIndex >= 0) {
        this.save.unlocked = Math.max(this.save.unlocked, Math.min(MAPS.length - 1, mapIndex + 1));
      }
      this.persist();
    }

    looplay.completeLevel({
      won,
      score,
      level: levelNumber,
      mapId: this.selectedMap.id,
      difficulty: this.difficultyKey,
      turns: this.turnCount
    });

    this.ui.controls.classList.add('hidden');
    this.ui.overlay.classList.remove('hidden');
    this.ui.showResult({ won, score, stars, map: this.selectedMap });
  }

  reviveAfterReward() {
    if (this.state !== 'over' || !this.player) return;

    this.state = 'playing';
    this.player.alive = true;
    this.player.hp = Math.max(1, Math.ceil(this.player.maxHp * 0.5));
    this.turn = 'player';
    this.activeMoveDir = 0;
    this.projectiles.length = 0;
    this.pendingExtraShots = 0;
    this.afterShotPending = false;
    this.ui.overlay.classList.add('hidden');
    this.ui.controls.classList.remove('hidden');
    this.updateHUD();
    this.setMessage('✨ Revived with 50% HP');

    const mapIndex = MAPS.findIndex((map) => map.id === this.selectedMap.id);
    const level = mapIndex >= 0 ? mapIndex + 1 : Math.max(1, (this.save.unlocked || 0) + 1);
    looplay.startSession({
      matchId: crypto.randomUUID(),
      mapId: this.selectedMap.id,
      level,
      difficulty: this.difficultyKey,
      resumedAfterReward: true
    });
  }

  setMessage(text) {
    this.message = text;
    this.messageTimer = 1.8;
  }

  updatePlayerMovement(dt) {
    if (!this.player || !this.player.alive) return;
    if (this.state !== 'playing' || this.turn !== 'player' || this.projectiles.length > 0) {
      this.player.isMoving = false;
      return;
    }

    if (this.activeMoveDir !== 0) {
      this.player.facing = this.activeMoveDir;

      if (this.player.stamina > 0) {
        const moveSpeed = 82; // px per second
        const staminaDrainRate = 42; // stamina units per second -> ~2.4 seconds of move
        const step = this.activeMoveDir * moveSpeed * dt;
        const targetX = clamp(this.player.x + step, 28, this.worldW - 28);

        // Check slope steepness to prevent climbing vertical crater walls
        const currY = this.terrainYPx(this.player.x);
        const targetY = this.terrainYPx(targetX);
        const deltaX = Math.abs(targetX - this.player.x);
        const deltaY = targetY - currY; // negative is uphill

        const isClimbingUphill = deltaY < 0;
        const slope = deltaX > 0.001 ? Math.abs(deltaY / deltaX) : 0;
        const maxClimbSlope = 1.9; // ~62 degrees

        let blockedBySlope = isClimbingUphill && slope > maxClimbSlope;

        // Also check if walking into an alive enemy
        let blockedByEnemy = false;
        if (this.enemies) {
          for (const e of this.enemies) {
            if (!e.alive) continue;
            if (Math.abs(targetX - e.x) < this.player.radius + e.radius - 2) {
              blockedByEnemy = true;
              break;
            }
          }
        }

        if (!blockedBySlope && !blockedByEnemy) {
          this.player.x = targetX;
          this.player.y = targetY - this.player.radius;
          this.player.stamina = Math.max(0, this.player.stamina - staminaDrainRate * dt);
          this.player.isMoving = true;
          this.player.walkTime = (this.player.walkTime || 0) + dt * 12;

          // Dust particles while walking
          this.player.dustTimer = (this.player.dustTimer || 0) + dt;
          if (this.player.dustTimer > 0.12) {
            this.player.dustTimer = 0;
            this.particles.push({
              x: this.player.x - this.player.facing * 8 + rand(-3, 3),
              y: this.player.y + this.player.radius - 2,
              vx: -this.player.facing * rand(12, 35),
              vy: rand(-8, -24),
              color: 'rgba(214, 211, 209, 0.7)',
              size: rand(2.5, 4.5),
              life: rand(0.25, 0.4),
              max: 0.4
            });
          }

          if (this.ui.staminaVal && this.ui.staminaFill) {
            const ratio = clamp(this.player.stamina / this.player.maxStamina, 0, 1);
            this.ui.staminaVal.textContent = Math.ceil(this.player.stamina);
            this.ui.staminaFill.style.width = `${ratio * 100}%`;
          }
        } else {
          this.player.isMoving = false;
        }
      } else {
        this.player.isMoving = false;
      }
    } else {
      this.player.isMoving = false;
    }
  }

  updateHUD() {
    if (!this.player) return;
    this.ui.playerHp.textContent = `${Math.ceil(this.player.hp)} / ${this.player.maxHp}`;
    this.ui.playerHpFill.style.width = `${clamp(this.player.hp / this.player.maxHp, 0, 1) * 100}%`;

    if (this.ui.staminaVal && this.ui.staminaFill) {
      const staminaRatio = clamp((this.player.stamina ?? 100) / (this.player.maxStamina ?? 100), 0, 1);
      this.ui.staminaVal.textContent = Math.ceil(this.player.stamina ?? 100);
      this.ui.staminaFill.style.width = `${staminaRatio * 100}%`;
    }

    const canAct = this.turn === 'player' && this.state === 'playing';
    if (this.ui.moveLeft) this.ui.moveLeft.disabled = !canAct;
    if (this.ui.moveRight) this.ui.moveRight.disabled = !canAct;

    const target = this.enemies?.find((e) => e.alive && e.boss) || this.enemies?.find((e) => e.alive);
    if (target) {
      this.ui.enemyHp.textContent = `${target.name} · ${Math.ceil(target.hp)}/${target.maxHp}`;
      this.ui.enemyHpFill.style.width = `${clamp(target.hp / target.maxHp, 0, 1) * 100}%`;
    } else {
      this.ui.enemyHp.textContent = 'Cleared';
      this.ui.enemyHpFill.style.width = '0%';
    }

    const abs = Math.abs(this.wind).toFixed(1);
    this.ui.wind.textContent = abs;
    if (this.ui.windArrow) {
      this.ui.windArrow.textContent = this.wind < -0.05 ? '←' : this.wind > 0.05 ? '→' : '•';
      this.ui.windArrow.style.transform = this.wind < -0.05 ? 'scaleX(-1)' : 'scaleX(1)';
    }

    this.ui.turn.textContent = this.turn === 'ad'
      ? 'Watching Ad…'
      : this.turn === 'player'
      ? 'Your Turn'
      : this.turn === 'enemy'
        ? 'Enemy Aiming…'
        : this.turn === 'done'
          ? 'Match Over'
          : 'In Flight';

    this.ui.fire.disabled = this.turn !== 'player';
    const canRequestRewardedAd = looplay.canRequestRewardedAd;
    this.ui.heal.disabled = this.turn !== 'player' || (this.items.heal <= 0 && !canRequestRewardedAd);
    this.ui.double.disabled = this.turn !== 'player' || (this.items.double <= 0 && !canRequestRewardedAd);
    this.ui.shield.disabled = this.turn !== 'player' || (this.items.shield <= 0 && !canRequestRewardedAd);
    this.ui.heal.innerHTML = `❤️<span>${this.items.heal > 0 ? this.items.heal : canRequestRewardedAd ? 'AD' : '0'}</span>`;
    this.ui.double.innerHTML = `✨<span>${this.items.double > 0 ? this.items.double : canRequestRewardedAd ? 'AD' : '0'}</span>`;
    this.ui.shield.innerHTML = `🛡️<span>${this.items.shield > 0 ? this.items.shield : canRequestRewardedAd ? 'AD' : '0'}</span>`;
    this.ui.heal.title = this.items.heal > 0 ? 'Heal' : 'Watch an ad to get Heal';
    this.ui.double.title = this.items.double > 0 ? 'Double Shot' : 'Watch an ad to get Double Shot';
    this.ui.shield.title = this.items.shield > 0 ? 'Shield' : 'Watch an ad to get Shield';
    this.ui.heal.setAttribute('aria-label', this.ui.heal.title);
    this.ui.double.setAttribute('aria-label', this.ui.double.title);
    this.ui.shield.setAttribute('aria-label', this.ui.shield.title);
  }

  drawBackground(ctx) {
    const w = this.worldW;
    const h = this.worldH;
    const map = this.selectedMap;

    // Sky gradient spanning extended area
    const skyGrad = ctx.createLinearGradient(0, -200, 0, h);
    skyGrad.addColorStop(0, map.sky);
    skyGrad.addColorStop(1, this.mixColor(map.sky, '#ffffff', 0.22));
    ctx.fillStyle = skyGrad;
    ctx.fillRect(-300, -300, w + 600, h + 500);

    // Distant mountain range (parallax 0.18x)
    const px1 = (this.camera.x - w / 2) * 0.18;
    ctx.fillStyle = this.mixColor(map.sky, map.soil, 0.32);
    ctx.beginPath();
    ctx.moveTo(-300, h);
    for (let x = -300; x <= w + 300; x += 25) {
      const my = h * 0.60 + Math.sin((x + px1) * 0.0035 + 1.2) * 65 + Math.sin((x + px1) * 0.008) * 28;
      ctx.lineTo(x, my);
    }
    ctx.lineTo(w + 300, h);
    ctx.closePath();
    ctx.fill();

    // Midground hills (parallax 0.38x)
    const px2 = (this.camera.x - w / 2) * 0.38;
    ctx.fillStyle = this.mixColor(map.sky, map.ground, 0.44);
    ctx.beginPath();
    ctx.moveTo(-300, h);
    for (let x = -300; x <= w + 300; x += 20) {
      const my = h * 0.68 + Math.sin((x + px2) * 0.006 + 0.4) * 45 + Math.sin((x + px2) * 0.015) * 18;
      ctx.lineTo(x, my);
    }
    ctx.lineTo(w + 300, h);
    ctx.closePath();
    ctx.fill();

    // Floating fluffy clouds
    ctx.globalAlpha = 0.22;
    ctx.fillStyle = '#ffffff';
    for (let i = 0; i < 14; i++) {
      const cx = (((i * 211 + this.turnCount * 25) % 1600) - 200);
      const cy = ((i * 137 + 31) % 400) * 0.55;
      ctx.beginPath();
      ctx.arc(cx, cy, 14 + (i % 4) * 9, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // Aerodynamic WindShot Cyan Wind Ribbons & Swirls
    const animOffset = this.turnCount * 45 + (performance.now() / 1000) * 22 * (Math.abs(this.wind) > 0.05 ? Math.sign(this.wind) : 1);
    const windDir = this.wind >= 0 ? 1 : -1;
    const streamlines = [
      { y: h * 0.12, len: 170 + Math.abs(this.wind) * 80, speedMult: 1.0, alpha: 0.36, width: 3.2 },
      { y: h * 0.23, len: 230 + Math.abs(this.wind) * 110, speedMult: 1.45, alpha: 0.46, width: 3.8 },
      { y: h * 0.35, len: 150 + Math.abs(this.wind) * 60, speedMult: 0.85, alpha: 0.28, width: 2.6 }
    ];

    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    for (let s = 0; s < streamlines.length; s++) {
      const line = streamlines[s];
      const startX = (((s * 520 + animOffset * line.speedMult * windDir) % (w + 400)) - 200);
      const endX = startX + windDir * line.len;
      const midX = (startX + endX) / 2;
      const wave = Math.sin(startX * 0.008 + s * 1.5) * 18;

      // Glowing outer cyan stroke
      ctx.strokeStyle = `rgba(56, 189, 248, ${line.alpha * 0.45})`;
      ctx.lineWidth = line.width + 4;
      ctx.beginPath();
      ctx.moveTo(startX, line.y + wave);
      ctx.quadraticCurveTo(midX, line.y - wave * 1.6, endX, line.y + wave * 0.5);
      ctx.stroke();

      // Bright inner white-cyan core
      ctx.strokeStyle = `rgba(224, 242, 254, ${line.alpha})`;
      ctx.lineWidth = line.width;
      ctx.beginPath();
      ctx.moveTo(startX, line.y + wave);
      ctx.quadraticCurveTo(midX, line.y - wave * 1.6, endX, line.y + wave * 0.5);
      ctx.stroke();

      // Aerodynamic arrow head at ribbon tip
      const arrowLen = 11;
      const targetY = line.y + wave * 0.5;
      const ctrlY = line.y - wave * 1.6;
      const arrowAngle = Math.atan2(targetY - ctrlY, endX - midX);
      ctx.fillStyle = `rgba(224, 242, 254, ${Math.min(1, line.alpha * 1.3)})`;
      ctx.beginPath();
      ctx.moveTo(endX, targetY);
      ctx.lineTo(
        endX - Math.cos(arrowAngle - 0.42) * arrowLen,
        targetY - Math.sin(arrowAngle - 0.42) * arrowLen
      );
      ctx.lineTo(
        endX - Math.cos(arrowAngle + 0.42) * arrowLen,
        targetY - Math.sin(arrowAngle + 0.42) * arrowLen
      );
      ctx.closePath();
      ctx.fill();

      // Stylized wind spiral curl at ribbon origin
      if (Math.abs(this.wind) > 0.08) {
        ctx.strokeStyle = `rgba(56, 189, 248, ${line.alpha * 0.75})`;
        ctx.lineWidth = line.width * 0.8;
        ctx.beginPath();
        for (let a = 0; a < Math.PI * 1.8; a += 0.28) {
          const sr = (1 - a / (Math.PI * 2)) * 12;
          const px = startX - windDir * Math.cos(a) * sr;
          const py = line.y + wave - Math.sin(a) * sr;
          if (a === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  mixColor(hexA, hexB, amount) {
    const a = hexA.replace('#', '');
    const b = hexB.replace('#', '');
    const pa = [0, 2, 4].map((i) => parseInt(a.slice(i, i + 2), 16));
    const pb = [0, 2, 4].map((i) => parseInt(b.slice(i, i + 2), 16));
    const out = pa.map((v, i) => Math.round(lerp(v, pb[i], amount)).toString(16).padStart(2, '0'));
    return `#${out.join('')}`;
  }

  drawTerrain(ctx) {
    const w = this.worldW;
    const h = this.worldH;
    const map = this.selectedMap;

    // Deep soil body
    ctx.beginPath();
    ctx.moveTo(0, h);
    for (let x = 0; x <= w; x += 4) ctx.lineTo(x, this.terrainYPx(x));
    ctx.lineTo(w, h);
    ctx.closePath();

    const dirt = ctx.createLinearGradient(0, h * 0.45, 0, h);
    dirt.addColorStop(0, map.soil);
    dirt.addColorStop(0.65, this.mixColor(map.soil, '#0f172a', 0.28));
    dirt.addColorStop(1, this.mixColor(map.soil, '#020617', 0.65));
    ctx.fillStyle = dirt;
    ctx.fill();

    // Rocky strata pattern lines
    ctx.save();
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.12)';
    ctx.lineWidth = 3;
    for (let j = 1; j <= 3; j++) {
      ctx.beginPath();
      for (let x = 0; x <= w; x += 10) {
        const y = this.terrainYPx(x) + j * 48 + Math.sin(x * 0.02) * 8;
        if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    ctx.restore();

    // Grass / Top crust highlight (thick)
    ctx.beginPath();
    for (let x = 0; x <= w; x += 4) {
      const y = this.terrainYPx(x);
      if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = this.mixColor(map.ground, '#ffffff', 0.16);
    ctx.lineWidth = 10;
    ctx.lineJoin = 'round';
    ctx.stroke();

    // Grass / Surface rim (thin sharp edge)
    ctx.beginPath();
    for (let x = 0; x <= w; x += 4) {
      const y = this.terrainYPx(x) + 3;
      if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = map.ground;
    ctx.lineWidth = 4;
    ctx.stroke();
  }

  drawPlayer(ctx, actor, time) {
    if (!actor.alive) return;
    const isHit = actor.flash > 0;
    const recoil = actor.recoil || 0;
    const isMoving = !!actor.isMoving;
    const walkTime = actor.walkTime || 0;

    const idleBob = Math.sin(time * 4) * 1.5;
    const walkBob = Math.abs(Math.sin(walkTime * 1.6)) * 2.2;
    const bob = isMoving ? -walkBob : idleBob;
    const waddleRot = isMoving ? Math.sin(walkTime * 1.6) * 0.06 : 0;

    ctx.save();
    ctx.translate(actor.x, actor.y + bob);

    // Ground Shadow under wheels
    ctx.fillStyle = 'rgba(0, 0, 0, 0.28)';
    ctx.beginPath();
    ctx.ellipse(0, actor.radius + 3 - bob, actor.radius * 1.25, 6, 0, 0, Math.PI * 2);
    ctx.fill();

    // Mirror horizontally based on facing direction (-1 = left, 1 = right)
    const facing = actor.facing || 1;
    ctx.scale(facing, 1);
    if (waddleRot !== 0) ctx.rotate(waddleRot);

    // Subtle breathing / impact squash & stretch
    const scaleX = isHit ? 1.15 : 1 + Math.sin(time * 3.5) * 0.02;
    const scaleY = isHit ? 0.88 : 1 - Math.sin(time * 3.5) * 0.02;
    ctx.scale(scaleX, scaleY);

    // Heavy Black Rubber Tires with Steel Hubcaps & Rotating Spokes
    const wheelRot = isMoving ? walkTime * 7 : 0;
    const wheelY = actor.radius - 2;
    const wheelPositions = [-10, 8];

    for (const wx of wheelPositions) {
      ctx.save();
      ctx.translate(wx, wheelY);
      ctx.rotate(wheelRot);

      // Outer rubber tire
      ctx.fillStyle = '#090d16';
      ctx.beginPath();
      ctx.arc(0, 0, 7.5, 0, Math.PI * 2);
      ctx.fill();

      // Tire tread ring
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 1.6;
      ctx.stroke();

      // Inner steel hubcap
      ctx.fillStyle = '#334155';
      ctx.beginPath();
      ctx.arc(0, 0, 4.2, 0, Math.PI * 2);
      ctx.fill();

      // Rotating spoke cross / bolts
      ctx.strokeStyle = '#94a3b8';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(-3, 0); ctx.lineTo(3, 0);
      ctx.moveTo(0, -3); ctx.lineTo(0, 3);
      ctx.stroke();

      // Center rivet
      ctx.fillStyle = '#f8fafc';
      ctx.beginPath();
      ctx.arc(0, 0, 1.2, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    }

    // Heavy Artillery Cannon (mounted forward, rotates with aim angle, recoils when fired)
    ctx.save();
    ctx.translate(4, -2);
    const rad = (-this.angle * Math.PI) / 180;
    ctx.rotate(rad);
    const recoilOffset = -recoil * 10;
    ctx.translate(recoilOffset, 0);

    // Steel cannon base mount
    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.arc(0, 0, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Heavy cylindrical blue cannon barrel
    const barrelGrad = ctx.createLinearGradient(0, -6, 0, 6);
    barrelGrad.addColorStop(0, '#60a5fa');
    barrelGrad.addColorStop(0.35, '#2563eb');
    barrelGrad.addColorStop(0.8, '#1d4ed8');
    barrelGrad.addColorStop(1, '#1e3a8a');
    ctx.fillStyle = barrelGrad;
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(2, -6, 23, 12, 2.5);
    } else {
      ctx.rect(2, -6, 23, 12);
    }
    ctx.fill();

    // Top metal gloss highlight on barrel
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.fillRect(4, -5, 19, 2);

    // Wide glowing cyan muzzle collar ring (signature from logo)
    const muzzleGrad = ctx.createLinearGradient(0, -8, 0, 8);
    muzzleGrad.addColorStop(0, '#e0f2fe');
    muzzleGrad.addColorStop(0.5, '#38bdf8');
    muzzleGrad.addColorStop(1, '#0284c7');
    ctx.fillStyle = muzzleGrad;
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(22, -8, 5.5, 16, 2);
    } else {
      ctx.rect(22, -8, 5.5, 16);
    }
    ctx.fill();

    // Glowing cyan inner muzzle aperture
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.ellipse(26.5, 0, 1.8, 6.5, 0, 0, Math.PI * 2);
    ctx.fill();

    // Energy charge glow inside muzzle
    ctx.fillStyle = '#38bdf8';
    ctx.beginPath();
    ctx.ellipse(26.2, 0, 1.2, 4.5, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore(); // Restore Cannon

    // Round Electric Blue Sphere Body (Chassis)
    const bodyGrad = ctx.createRadialGradient(-5, -6, 3, 0, 0, actor.radius + 2);
    if (isHit) {
      bodyGrad.addColorStop(0, '#ffffff');
      bodyGrad.addColorStop(0.4, '#93c5fd');
      bodyGrad.addColorStop(1, '#ef4444');
    } else {
      bodyGrad.addColorStop(0, '#7dd3fc');
      bodyGrad.addColorStop(0.35, '#38bdf8');
      bodyGrad.addColorStop(0.75, '#2563eb');
      bodyGrad.addColorStop(1, '#1e3a8a');
    }
    ctx.fillStyle = bodyGrad;
    ctx.beginPath();
    ctx.arc(0, 0, actor.radius, 0, Math.PI * 2);
    ctx.fill();

    // Thick crisp dark contour
    ctx.strokeStyle = '#090d16';
    ctx.lineWidth = 2.2;
    ctx.stroke();

    // Top-left glossy specular highlight streak (cartoon sheen)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.beginPath();
    ctx.ellipse(-5, -8, 7, 3.5, -0.4, 0, Math.PI * 2);
    ctx.fill();

    // Determined Cartoon Battle Eye (Large eye with bold black angled brow from logo & thumbnail)
    const blink = Math.sin(time * 0.7) > 0.96;
    if (blink) {
      ctx.strokeStyle = '#090d16';
      ctx.lineWidth = 2.8;
      ctx.beginPath();
      ctx.arc(2, -2, 5, 0.2, Math.PI - 0.2);
      ctx.stroke();
    } else {
      // Big white eyeball with thick black contour
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.ellipse(2.5, -2, 6.2, 7.5, 0.1, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#090d16';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Determined black pupil looking forward
      ctx.fillStyle = '#090d16';
      ctx.beginPath();
      ctx.arc(4.2, -2, 3.8, 0, Math.PI * 2);
      ctx.fill();

      // Deep cyan iris rim
      ctx.strokeStyle = '#0284c7';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Specular highlight sparkle dots
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(3.4, -4.2, 1.8, 0, Math.PI * 2);
      ctx.arc(5.4, -0.2, 0.9, 0, Math.PI * 2);
      ctx.fill();
    }

    // Bold Black Angled Battle Eyebrow (slanted sharply down towards cannon)
    ctx.fillStyle = '#090d16';
    ctx.beginPath();
    ctx.moveTo(-3, -11.5);
    ctx.lineTo(8.5, -6.5);
    ctx.lineTo(9.5, -8.5);
    ctx.lineTo(-2, -13.5);
    ctx.closePath();
    ctx.fill();

    // Shield Aura
    if (this.playerShield > 0) {
      const shieldTime = time * 2;
      ctx.save();
      ctx.strokeStyle = '#38bdf8';
      ctx.lineWidth = 3;
      ctx.shadowColor = '#38bdf8';
      ctx.shadowBlur = 14;
      ctx.fillStyle = 'rgba(56, 189, 248, 0.18)';
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (i * Math.PI) / 3 + shieldTime * 0.4;
        const r = actor.radius + 12 + Math.sin(shieldTime + i) * 1.5;
        const x = Math.cos(a) * r;
        const y = Math.sin(a) * r;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }

    ctx.restore();

    // Floating Capsule Pill HP Bar
    this.drawActorHP(ctx, actor, '#38bdf8', '#22c55e');
  }

  drawEnemy(ctx, actor, time) {
    if (!actor.alive) return;
    const isHit = actor.flash > 0;
    const bob = Math.sin(time * 3.5 + actor.x) * 1.8;

    ctx.save();
    ctx.translate(actor.x, actor.y + bob);

    // Ground Shadow under bot
    ctx.fillStyle = 'rgba(0, 0, 0, 0.28)';
    ctx.beginPath();
    ctx.ellipse(0, actor.radius + 3 - bob, actor.radius * 1.15, 5.5, 0, 0, Math.PI * 2);
    ctx.fill();

    const scaleX = isHit ? 1.15 : 1;
    const scaleY = isHit ? 0.88 : 1;
    ctx.scale(scaleX, scaleY);

    // Mini Rubber Wheels under orange bot
    const wheelPositions = [-8, 7];
    for (const wx of wheelPositions) {
      ctx.fillStyle = '#090d16';
      ctx.beginPath();
      ctx.arc(wx, actor.radius - 2, 5.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 1.2;
      ctx.stroke();
      ctx.fillStyle = '#475569';
      ctx.beginPath();
      ctx.arc(wx, actor.radius - 2, 2.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#94a3b8';
      ctx.beginPath();
      ctx.arc(wx, actor.radius - 2, 1, 0, Math.PI * 2);
      ctx.fill();
    }

    // Forehead Mounted Dark Cannon (aiming towards player at left)
    ctx.save();
    ctx.translate(-5, -6);
    ctx.rotate(0.35); // points down-left toward player
    const barrelGrad = ctx.createLinearGradient(0, -4, 0, 4);
    barrelGrad.addColorStop(0, '#475569');
    barrelGrad.addColorStop(0.5, '#1e293b');
    barrelGrad.addColorStop(1, '#0f172a');
    ctx.fillStyle = barrelGrad;
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(-16, -3.5, 15, 7, 2);
    } else {
      ctx.rect(-16, -3.5, 15, 7);
    }
    ctx.fill();
    ctx.strokeStyle = '#090d16';
    ctx.lineWidth = 1.2;
    ctx.stroke();

    // Dark flared muzzle ring
    ctx.fillStyle = '#090d16';
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(-18, -4.5, 3.5, 9, 1.5);
    } else {
      ctx.rect(-18, -4.5, 3.5, 9);
    }
    ctx.fill();
    ctx.restore();

    // Round Orange Sphere Body (with 3D gradient matching thumbnail)
    const bodyGrad = ctx.createRadialGradient(-4, -5, 3, 0, 0, actor.radius + 2);
    if (isHit) {
      bodyGrad.addColorStop(0, '#ffffff');
      bodyGrad.addColorStop(0.4, '#fed7aa');
      bodyGrad.addColorStop(1, '#ef4444');
    } else {
      bodyGrad.addColorStop(0, '#fef08a');
      bodyGrad.addColorStop(0.25, '#fdba74');
      bodyGrad.addColorStop(0.65, '#f97316');
      bodyGrad.addColorStop(0.9, '#ea580c');
      bodyGrad.addColorStop(1, '#9a3412');
    }
    ctx.fillStyle = bodyGrad;
    ctx.beginPath();
    ctx.arc(0, 0, actor.radius, 0, Math.PI * 2);
    ctx.fill();

    // Crisp dark contour
    ctx.strokeStyle = '#090d16';
    ctx.lineWidth = 2.2;
    ctx.stroke();

    // Glossy top specular reflection
    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.beginPath();
    ctx.ellipse(-4, -7, 6, 3, -0.3, 0, Math.PI * 2);
    ctx.fill();

    // Determined Cartoon Battle Eye (facing left toward player)
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.ellipse(-4, -1, 5.5, 6.5, -0.1, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#090d16';
    ctx.lineWidth = 1.8;
    ctx.stroke();

    // Focused black pupil
    ctx.fillStyle = '#090d16';
    ctx.beginPath();
    ctx.arc(-5.5, -1, 3.2, 0, Math.PI * 2);
    ctx.fill();

    // Specular catchlight dots
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(-6.5, -2.8, 1.4, 0, Math.PI * 2);
    ctx.arc(-4.2, 0.5, 0.7, 0, Math.PI * 2);
    ctx.fill();

    // Bold Black Angled Eyebrow (slanted sharply down towards front)
    ctx.fillStyle = '#090d16';
    ctx.beginPath();
    ctx.moveTo(2, -9);
    ctx.lineTo(-9.5, -5.5);
    ctx.lineTo(-10.5, -7.5);
    ctx.lineTo(1, -11);
    ctx.closePath();
    ctx.fill();

    // Cheek blush
    ctx.fillStyle = 'rgba(239, 68, 68, 0.35)';
    ctx.beginPath();
    ctx.ellipse(-4, 5, 3.2, 1.8, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();

    // Floating Capsule Pill HP Bar
    this.drawActorHP(ctx, actor, '#f97316', '#ef4444');
  }

  drawBoss(ctx, actor, time) {
    if (!actor.alive) return;
    const isHit = actor.flash > 0;
    const bob = Math.sin(time * 2.8) * 2.0;

    ctx.save();
    ctx.translate(actor.x, actor.y + bob);

    // Heavy Boss Shadow with Pulsing Fire Aura
    ctx.fillStyle = 'rgba(0,0,0,0.36)';
    ctx.beginPath();
    ctx.ellipse(0, actor.radius + 6 - bob, actor.radius * 1.35, 9, 0, 0, Math.PI * 2);
    ctx.fill();

    // Fiery boss threat aura
    ctx.save();
    ctx.shadowColor = '#f97316';
    ctx.shadowBlur = 18;
    ctx.strokeStyle = 'rgba(249, 115, 22, 0.35)';
    ctx.lineWidth = 3.5;
    ctx.beginPath();
    ctx.arc(0, 0, actor.radius + 5 + Math.sin(time * 4.5) * 2, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();

    const scaleX = isHit ? 1.12 : 1;
    const scaleY = isHit ? 0.90 : 1;
    ctx.scale(scaleX, scaleY);

    // Heavy Caterpillar Tread Base (Continuous armored tracks with road wheels)
    const treadY = actor.radius + 1;
    ctx.save();
    // Dark steel chassis bed
    ctx.fillStyle = '#090d16';
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(-28, treadY - 7, 56, 14, 7);
    } else {
      ctx.rect(-28, treadY - 7, 56, 14);
    }
    ctx.fill();
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Road wheels / Rollers (4 steel rollers inside the track)
    const rollerX = [-20, -7, 7, 20];
    for (const rx of rollerX) {
      ctx.fillStyle = '#334155';
      ctx.beginPath();
      ctx.arc(rx, treadY, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#64748b';
      ctx.beginPath();
      ctx.arc(rx, treadY, 2.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#f8fafc';
      ctx.beginPath();
      ctx.arc(rx, treadY, 1, 0, Math.PI * 2);
      ctx.fill();
    }

    // Segmented tread track pads
    ctx.strokeStyle = '#475569';
    ctx.lineWidth = 1.5;
    for (let tx = -26; tx <= 26; tx += 6) {
      ctx.beginPath();
      ctx.moveTo(tx, treadY - 7);
      ctx.lineTo(tx, treadY - 5);
      ctx.moveTo(tx, treadY + 5);
      ctx.lineTo(tx, treadY + 7);
      ctx.stroke();
    }
    ctx.restore();

    // Massive Red-and-Gold Siege Cannon (Mounted forward, aiming left at player)
    ctx.save();
    ctx.translate(-10, 0);
    ctx.rotate(0.32);

    // Deep crimson & fiery red barrel gradient
    const cannonGrad = ctx.createLinearGradient(0, -9, 0, 9);
    cannonGrad.addColorStop(0, '#f87171');
    cannonGrad.addColorStop(0.35, '#dc2626');
    cannonGrad.addColorStop(0.75, '#b91c1c');
    cannonGrad.addColorStop(1, '#7f1d1d');
    ctx.fillStyle = cannonGrad;
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(-36, -8, 28, 16, 3);
    } else {
      ctx.rect(-36, -8, 28, 16);
    }
    ctx.fill();
    ctx.strokeStyle = '#090d16';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Gold reinforcement rings (from thumbnail)
    ctx.fillStyle = '#fbbf24';
    ctx.fillRect(-26, -9, 4.5, 18);
    ctx.fillRect(-14, -9, 4.5, 18);

    // Huge flared golden muzzle rim ring
    const bossMuzzleGrad = ctx.createLinearGradient(0, -11, 0, 11);
    bossMuzzleGrad.addColorStop(0, '#fef08a');
    bossMuzzleGrad.addColorStop(0.5, '#fbbf24');
    bossMuzzleGrad.addColorStop(1, '#b45309');
    ctx.fillStyle = bossMuzzleGrad;
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(-40, -10.5, 6, 21, 2.5);
    } else {
      ctx.rect(-40, -10.5, 6, 21);
    }
    ctx.fill();
    ctx.strokeStyle = '#090d16';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Dark muzzle cavity with glowing fire core
    ctx.fillStyle = '#090d16';
    ctx.beginPath();
    ctx.ellipse(-39, 0, 2.5, 8.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ef4444';
    ctx.beginPath();
    ctx.ellipse(-38.5, 0, 1.5, 6, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore(); // Restore Cannon

    // Massive Round King Orange Tank Body
    const bodyGrad = ctx.createRadialGradient(-7, -8, 5, 0, 0, actor.radius + 3);
    if (isHit) {
      bodyGrad.addColorStop(0, '#ffffff');
      bodyGrad.addColorStop(0.4, '#fed7aa');
      bodyGrad.addColorStop(1, '#dc2626');
    } else {
      bodyGrad.addColorStop(0, '#fef08a');
      bodyGrad.addColorStop(0.25, '#fdba74');
      bodyGrad.addColorStop(0.65, '#f97316');
      bodyGrad.addColorStop(0.9, '#ea580c');
      bodyGrad.addColorStop(1, '#7c2d12');
    }
    ctx.fillStyle = bodyGrad;
    ctx.beginPath();
    ctx.arc(0, 0, actor.radius, 0, Math.PI * 2);
    ctx.fill();

    // Thick crisp dark contour
    ctx.strokeStyle = '#090d16';
    ctx.lineWidth = 3;
    ctx.stroke();

    // Specular gloss reflection curve
    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.beginPath();
    ctx.ellipse(-8, -12, 10, 5, -0.35, 0, Math.PI * 2);
    ctx.fill();

    // Royal 5-Point Golden Crown with Rubies (from thumbnail)
    ctx.save();
    const crownGrad = ctx.createLinearGradient(0, -actor.radius - 22, 0, -actor.radius);
    crownGrad.addColorStop(0, '#fef08a');
    crownGrad.addColorStop(0.4, '#facc15');
    crownGrad.addColorStop(1, '#ca8a04');
    ctx.fillStyle = crownGrad;
    ctx.beginPath();
    ctx.moveTo(-18, -actor.radius + 3);
    ctx.lineTo(-14, -actor.radius - 17);
    ctx.lineTo(-7, -actor.radius - 7);
    ctx.lineTo(0, -actor.radius - 21);
    ctx.lineTo(7, -actor.radius - 7);
    ctx.lineTo(14, -actor.radius - 17);
    ctx.lineTo(18, -actor.radius + 3);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#090d16';
    ctx.lineWidth = 2.2;
    ctx.stroke();

    // Crown base trim band
    ctx.fillStyle = '#eab308';
    ctx.fillRect(-17, -actor.radius - 1, 34, 4);

    // Glowing ruby gems on crown points & center
    ctx.fillStyle = '#ef4444';
    ctx.beginPath();
    ctx.arc(0, -actor.radius - 13, 3, 0, Math.PI * 2);
    ctx.arc(-13, -actor.radius - 11, 2.2, 0, Math.PI * 2);
    ctx.arc(13, -actor.radius - 11, 2.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(-0.8, -actor.radius - 14, 1, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Fierce Warlord Battle Eye (facing left at player)
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.ellipse(-7, -3, 8.5, 9.5, -0.1, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#090d16';
    ctx.lineWidth = 2.5;
    ctx.stroke();

    // Glowing fiery iris
    ctx.fillStyle = '#b91c1c';
    ctx.beginPath();
    ctx.arc(-9, -3, 4.8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#090d16';
    ctx.beginPath();
    ctx.arc(-9.5, -3, 3.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(-11, -5.5, 1.8, 0, Math.PI * 2);
    ctx.arc(-7.5, -1, 0.9, 0, Math.PI * 2);
    ctx.fill();

    // Fierce Angled Black Warlord Eyebrow
    ctx.fillStyle = '#090d16';
    ctx.beginPath();
    ctx.moveTo(3, -15);
    ctx.lineTo(-15, -9);
    ctx.lineTo(-16.5, -12);
    ctx.lineTo(2, -18);
    ctx.closePath();
    ctx.fill();

    // Confident battle smirk
    ctx.strokeStyle = '#090d16';
    ctx.lineWidth = 2.4;
    ctx.beginPath();
    ctx.arc(-7, 7, 7, 0.25, Math.PI - 0.4);
    ctx.stroke();

    ctx.restore();

    // Floating Capsule Pill HP Bar for Boss
    this.drawActorHP(ctx, actor, '#f59e0b', '#dc2626', true);
  }

  drawActorHP(ctx, actor, colorPrimary, colorAccent, isBoss = false) {
    const p = actor;
    const hpW = isBoss ? 92 : 56;
    const hpH = isBoss ? 11 : 8;
    const x = p.x - hpW / 2;
    const y = p.y - actor.radius - (isBoss ? 35 : 24);
    const radius = hpH / 2;

    ctx.save();

    // Outer drop shadow bezel
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(x - 1.5, y - 1.5, hpW + 3, hpH + 3, radius + 1.5);
    } else {
      ctx.rect(x - 1.5, y - 1.5, hpW + 3, hpH + 3);
    }
    ctx.fill();

    // Deep obsidian track
    ctx.fillStyle = '#090d16';
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(x, y, hpW, hpH, radius);
    } else {
      ctx.rect(x, y, hpW, hpH);
    }
    ctx.fill();

    // Vibrant fill gradient
    const fillRatio = clamp(actor.hp / actor.maxHp, 0, 1);
    if (fillRatio > 0) {
      const fillW = Math.max(hpH, hpW * fillRatio);
      const grad = ctx.createLinearGradient(x, 0, x + fillW, 0);
      grad.addColorStop(0, colorAccent);
      grad.addColorStop(1, colorPrimary);
      ctx.fillStyle = grad;
      ctx.beginPath();
      if (typeof ctx.roundRect === 'function') {
        ctx.roundRect(x, y, fillW, hpH, radius);
      } else {
        ctx.rect(x, y, fillW, hpH);
      }
      ctx.fill();

      // Top candy gloss reflection shine (WindShot arcade finish)
      ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
      ctx.beginPath();
      if (typeof ctx.roundRect === 'function') {
        ctx.roundRect(x + 1, y + 1, fillW - 2, hpH * 0.38, radius);
      } else {
        ctx.rect(x + 1, y + 1, fillW - 2, hpH * 0.38);
      }
      ctx.fill();
    }

    // Border: gold rim for boss, crisp dark navy for normal
    ctx.strokeStyle = isBoss ? '#fbbf24' : 'rgba(255, 255, 255, 0.28)';
    ctx.lineWidth = isBoss ? 1.8 : 1.2;
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(x, y, hpW, hpH, radius);
    } else {
      ctx.rect(x, y, hpW, hpH);
    }
    ctx.stroke();

    if (isBoss) {
      ctx.font = '900 11px Outfit, Inter, system-ui';
      ctx.fillStyle = '#fef08a';
      ctx.textAlign = 'center';
      ctx.shadowColor = 'rgba(0,0,0,0.8)';
      ctx.shadowBlur = 4;
      ctx.fillText('👑 KING TANK', p.x, y - 5);
    }
    ctx.restore();
  }

  drawActor(ctx, actor, kind) {
    const time = performance.now() / 1000;
    if (kind === 'player') {
      this.drawPlayer(ctx, actor, time);
    } else if (actor.boss) {
      this.drawBoss(ctx, actor, time);
    } else {
      this.drawEnemy(ctx, actor, time);
    }
  }

  drawTrajectory(ctx) {
    if (this.state !== 'playing' || this.turn !== 'player') return;
    const start = this.actorPx(this.player);
    const speed = lerp(this.minSpeed, this.maxSpeed, this.power / 100);
    const rad = (this.angle * Math.PI) / 180;
    const facing = this.player.facing || 1;
    let x = start.x + 22 * facing;
    let y = start.y - 12;
    let vx = Math.cos(rad) * speed * facing;
    let vy = -Math.sin(rad) * speed;
    const gravity = this.baseGravity * (this.selectedMap.gravity ?? 1);
    const windAccel = this.wind * 50;

    const points = [];
    for (let i = 0; i < 52; i++) {
      const dt = 0.045;
      vx += windAccel * dt;
      vy += gravity * dt;
      x += vx * dt;
      y += vy * dt;
      points.push({ x, y });
      if (x < 0 || x > this.worldW || y > this.terrainYPx(x)) break;
    }

    if (points.length < 2) return;

    ctx.save();
    ctx.lineCap = 'round';

    // 1. Cyan outer halo glow stroke (WindShot signature arc)
    ctx.strokeStyle = 'rgba(56, 189, 248, 0.45)';
    ctx.lineWidth = 4.5;
    ctx.setLineDash([8, 8]);
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
    ctx.stroke();

    // 2. Bright white sharp dashed core
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.lineWidth = 2.2;
    ctx.setLineDash([8, 8]);
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
    ctx.stroke();

    // 3. Predicted impact landing reticle on terrain
    const last = points[points.length - 1];
    ctx.setLineDash([]);
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(last.x, last.y, 6.5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(last.x, last.y, 2.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  drawEffects(ctx) {
    for (const fx of this.effects) {
      const t = clamp(fx.age / fx.duration, 0, 1);
      const radius = lerp(8, fx.maxRadius, t);
      ctx.globalAlpha = 1 - t;
      ctx.fillStyle = t < 0.35 ? '#fff7d6' : '#fb923c';
      ctx.beginPath();
      ctx.arc(fx.x, fx.y, Math.max(4, radius * (1 - t * 0.45)), 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#fde68a';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(fx.x, fx.y, radius, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  draw() {
    const ctx = this.ctx;
    const screenW = this.logicalW;
    const screenH = this.logicalH;

    ctx.save();

    // Screen Shake
    if (this.shake > 0) {
      ctx.translate(rand(-this.shake, this.shake), rand(-this.shake, this.shake));
    }

    // World Rendering via Camera Transformation
    ctx.save();
    ctx.translate(screenW / 2, screenH / 2);
    ctx.scale(this.camera.zoom, this.camera.zoom);
    ctx.translate(-this.camera.x, -this.camera.y);

    this.drawBackground(ctx);
    this.drawTerrain(ctx);

    if (this.player) this.drawActor(ctx, this.player, 'player');
    if (this.enemies) for (const e of this.enemies) this.drawActor(ctx, e, 'enemy');
    this.drawTrajectory(ctx);

    // Projectiles: Metallic obsidian cannonball with glowing aura & trail
    for (const p of this.projectiles) {
      if (p.trail.length > 1) {
        ctx.save();
        ctx.lineCap = 'round';
        for (let i = 1; i < p.trail.length; i++) {
          const ratio = i / p.trail.length;
          ctx.globalAlpha = ratio * 0.38;
          ctx.strokeStyle = p.color;
          ctx.lineWidth = 2 + ratio * 6;
          ctx.beginPath();
          ctx.moveTo(p.trail[i - 1].x, p.trail[i - 1].y);
          ctx.lineTo(p.trail[i].x, p.trail[i].y);
          ctx.stroke();
        }
        ctx.restore();
      }

      // Blazing outer energy aura halo
      ctx.save();
      ctx.shadowColor = p.color;
      ctx.shadowBlur = 14;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius + 1.8, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Dark metallic cannonball body
      const ballGrad = ctx.createRadialGradient(p.x - 2, p.y - 2, 1, p.x, p.y, p.radius);
      ballGrad.addColorStop(0, '#64748b');
      ballGrad.addColorStop(0.35, '#334155');
      ballGrad.addColorStop(0.75, '#1e293b');
      ballGrad.addColorStop(1, '#090d16');
      ctx.fillStyle = ballGrad;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.fill();

      // Outer bezel contour
      ctx.strokeStyle = '#090d16';
      ctx.lineWidth = 1.2;
      ctx.stroke();

      // Crisp white specular glint dot (specular reflection from logo)
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(p.x - p.radius * 0.35, p.y - p.radius * 0.35, Math.max(1, p.radius * 0.32), 0, Math.PI * 2);
      ctx.fill();
    }

    this.drawEffects(ctx);

    // Particles
    for (const q of this.particles) {
      ctx.globalAlpha = clamp(q.life / q.max, 0, 1);
      ctx.fillStyle = q.color;
      ctx.fillRect(q.x - q.size / 2, q.y - q.size / 2, q.size, q.size);
    }
    ctx.globalAlpha = 1;

    // Floating combat text
    for (const ft of this.floatingTexts) {
      ctx.save();
      ctx.globalAlpha = ft.alpha;
      ctx.font = '900 15px Inter, system-ui';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(0,0,0,0.7)';
      ctx.fillText(ft.text, ft.x + 1, ft.y + 1);
      ctx.fillStyle = ft.color;
      ctx.fillText(ft.text, ft.x, ft.y);
      ctx.restore();
    }

    ctx.restore(); // Restore World Transformation

    // Screen Space UI Overlays
    if (this.messageTimer > 0 && this.message) {
      ctx.font = '800 14px Inter, system-ui';
      const width = Math.min(screenW - 24, ctx.measureText(this.message).width + 28);
      ctx.fillStyle = 'rgba(8,15,28,.82)';
      ctx.beginPath();
      if (typeof ctx.roundRect === 'function') {
        ctx.roundRect((screenW - width) / 2, 54, width, 36, 12);
      } else {
        ctx.rect((screenW - width) / 2, 54, width, 36);
      }
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.fillText(this.message, screenW / 2, 77);
    }

    ctx.restore(); // Restore Screen Shake
  }

  loop(now) {
    if (!this.running) return;
    const dt = Math.min(0.033, (now - this.last) / 1000 || 0);
    this.last = now;
    if (this.state === 'playing') this.update(dt);
    this.draw();
    requestAnimationFrame((t) => this.loop(t));
  }
}

export { MAPS, DIFFICULTIES, BIOMES, generateProceduralMap };
